import { generateId } from "@fleet/utils";
import { createEffect, createMemo, createSignal, For, Show, untrack } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { History, Plus } from "@fleet/icons";
import { useUndo } from "@/lib/undo";
import { EntityHistoryDialog } from "@/features/activity/components/entity-history-dialog";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { ClockEmptyIllustration } from "@/components/ui/illustrations";
import { showToast } from "@/components/ui/toast";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import { ensureWorkspace } from "@/features/chronos-sync/workspace";
import { fromLocalInputValue, toLocalInputValue } from "@/features/chronos-sync/helpers";
import { useWorkspace } from "@/features/chronos-sync/workspace-context";
import {
  and,
  application_log,
  type ApplicationLog,
  desc,
  eq,
  feed_event,
  type FeedEvent,
  isNull,
  project,
  type Project,
  tag,
  type Tag,
  task,
  type Task,
  time_entry,
  time_entry_tag,
  type TimeEntry,
  type TimeEntryTag,
} from "@time-tracker/powersync";
import {
  cn,
  liveApplicationLogFilters,
  liveFeedEventFilters,
  liveProjectFilters,
  liveTagFilters,
  liveTaskFilters,
  liveTimeEntryFilters,
  patchRowVerified,
  queryKeys,
} from "@time-tracker/shared";
import { TimelineRow, type TimelineRowItem } from "@/features/chronos-sync/components/timeline-row";

interface GroupedActivityBlock {
  id: string;
  type: "unassigned";
  startTime: string;
  endTime: string;
  durationSeconds: number;
  durationStr: string;
  appClassList: { name: string; duration: string; letter: string }[];
  description: string;
  logIds: string[];
}

// The row shape lives in `timeline-row.tsx` so the swipeable component and this
// view never drift; alias it locally to keep the existing call sites readable.
type TimelineItem = TimelineRowItem;

const t_focus_note = msg`Focus Note`;

const FIVE_MINUTES_S = 300;
const SECONDS_IN_MINUTE = 60;

const APP_COLOR_MODULO = 4;
const APP_COLOR_INDEX_0 = 0;
const APP_COLOR_INDEX_1 = 1;
const APP_COLOR_INDEX_2 = 2;
const APP_COLOR_INDEX_3 = 3;

const MAX_APPS_DISPLAYED = 5;
const MAX_TITLES_DISPLAYED = 3;
const MS_IN_MINUTE = 60_000;
const LOG_SAMPLE_INTERVAL_S = 30;
const MS_IN_SEC = 1000;

// Helper to get first letter of application name
const getAppLetter = (name: string): string => name.trim().charAt(0).toUpperCase() || "?";

const getDotColor = (color: string) => {
  if (color.startsWith("bg-")) {
    return color;
  }
  return "bg-blue-500";
};

/** Stable per-app accent class so the avatar stack cycles four brand colors. */
const appColorClass = (idx: number): string =>
  cn(
    idx % APP_COLOR_MODULO === APP_COLOR_INDEX_0 &&
      "bg-[#f1b12b]/15 text-[#f1b12b] border-[#f1b12b]/30",
    idx % APP_COLOR_MODULO === APP_COLOR_INDEX_1 &&
      "bg-[#ebb2ff]/15 text-[#ebb2ff] border-[#ebb2ff]/30",
    idx % APP_COLOR_MODULO === APP_COLOR_INDEX_2 &&
      "bg-[#ceffdf]/15 text-[#ceffdf] border-[#ceffdf]/30",
    idx % APP_COLOR_MODULO === APP_COLOR_INDEX_3 &&
      "bg-amber-500/15 text-amber-400 border-amber-500/30",
  );

interface LogGroupState {
  logIds: string[];
  startTime: string;
  endTime: string;
  appDurationMap: Map<string, number>;
  titles: Set<string>;
}

const createInitialGroup = (log: ApplicationLog, logEnd: number): LogGroupState => ({
  logIds: [log.id],
  startTime: log.start_time ?? "",
  endTime: new Date(logEnd).toISOString(),
  appDurationMap: new Map([
    [log.application_name ?? "", log.duration_seconds ?? LOG_SAMPLE_INTERVAL_S],
  ]),
  titles: new Set(log.window_title ? [log.window_title] : []),
});

const addToGroup = (
  group: LogGroupState,
  log: ApplicationLog,
  logEnd: number,
  lastGroupEnd: number,
) => {
  group.logIds.push(log.id);
  group.endTime = new Date(Math.max(lastGroupEnd, logEnd)).toISOString();
  const appName = log.application_name ?? "";
  group.appDurationMap.set(
    appName,
    (group.appDurationMap.get(appName) ?? 0) + (log.duration_seconds ?? LOG_SAMPLE_INTERVAL_S),
  );
  if (log.window_title) {
    group.titles.add(log.window_title);
  }
};

export function ActivityTimelineView() {
  const { userId } = useSession();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { activeWorkspaceId } = useWorkspace();
  const { t } = useI18n();
  const { registerUndo } = useUndo();
  const [historyOpen, setHistoryOpen] = createSignal(false);

  // Deep link for time-entry dialogs (this component lives in both
  // `/chronos` and `/chronos/timeline`, so we use `strict:false` + `to:"."`
  // to write to the active route while preserving the remaining params, e.g. `?ws=`):
  //   - Add entry → ?dialog=entry-new
  //   - Edit entry → ?dialog=entry-edit&id=<entryId>
  //   - Delete entry → ?confirm=delete-entry&id=<entryId>
  const urlSearch = useSearch({ strict: false });
  const navigate = useNavigate();

  const entryUrlDialog = (): string | undefined => {
    const v = (urlSearch() as { dialog?: unknown }).dialog;
    return typeof v === "string" ? v : undefined;
  };
  const entryUrlConfirm = (): string | undefined => {
    const v = (urlSearch() as { confirm?: unknown }).confirm;
    return typeof v === "string" ? v : undefined;
  };
  const entryUrlId = (): string | undefined => {
    const v = (urlSearch() as { id?: unknown }).id;
    return typeof v === "string" && v !== "" ? v : undefined;
  };

  const setEntryUrl = (params: {
    dialog?: "entry-new" | "entry-edit";
    confirm?: "delete-entry";
    id?: string;
  }) => {
    void navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        dialog: params.dialog,
        confirm: params.confirm,
        id: params.id,
      }),
      replace: true,
    });
  };

  const [activePopoverId, setActivePopoverId] = createSignal<string | null>(null);

  // 1. Subscribe to projects of the active workspace
  const projects = createPowerSyncQuery<Project>({
    queryKey: () => queryKeys.chronos.projectsList(userId(), activeWorkspaceId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(project)
        .where(and(eq(project.workspace_id, activeWorkspaceId()), ...liveProjectFilters()));
    },
    enabled: () => Boolean(drizzle()) && Boolean(activeWorkspaceId()),
    suspense: false,
  });

  // 2. Subscribe to raw application logs
  const appLogs = createPowerSyncQuery<ApplicationLog>({
    queryKey: () => queryKeys.chronos.timeline.rawAppLogs(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(application_log)
        .where(
          and(
            eq(application_log.user_id, userId()),
            isNull(application_log.assigned_time_entry_id),
            ...liveApplicationLogFilters(),
          ),
        )
        .orderBy(desc(application_log.start_time));
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  // 3. Subscribe to registered time entries
  const timeEntries = createPowerSyncQuery<TimeEntry>({
    queryKey: () => queryKeys.chronos.timeline.timeEntriesHistory(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(time_entry)
        .where(and(eq(time_entry.user_id, userId()), ...liveTimeEntryFilters()))
        .orderBy(desc(time_entry.start_time));
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  // 3a-bis. Tasks (to display the name and populate the dialog selector)
  const tasksQuery = createPowerSyncQuery<Task>({
    queryKey: () => queryKeys.chronos.timeline.tasks(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(task)
        .where(and(...liveTaskFilters()));
    },
    enabled: () => Boolean(drizzle()),
    suspense: false,
  });

  const taskNameById = createMemo(() => {
    const map = new Map<string, string>();
    for (const row of tasksQuery.data ?? []) {
      if (row.name) {
        map.set(row.id, row.name);
      }
    }
    return map;
  });

  // Active workspace tags and entry↔tag associations
  const tagsQuery = createPowerSyncQuery<Tag>({
    queryKey: () => queryKeys.chronos.timeline.tags(activeWorkspaceId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(tag)
        .where(and(eq(tag.workspace_id, activeWorkspaceId()), ...liveTagFilters()));
    },
    enabled: () => Boolean(drizzle()) && Boolean(activeWorkspaceId()),
    suspense: false,
  });

  const entryTagsQuery = createPowerSyncQuery<TimeEntryTag>({
    queryKey: () => queryKeys.chronos.timeline.entryTags(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance.select().from(time_entry_tag);
    },
    enabled: () => Boolean(drizzle()),
    suspense: false,
  });

  const tagById = createMemo(() => {
    const map = new Map<string, Tag>();
    for (const row of tagsQuery.data ?? []) {
      map.set(row.id, row);
    }
    return map;
  });

  const tagsByEntryId = createMemo(() => {
    const map = new Map<string, Tag[]>();
    for (const link of entryTagsQuery.data ?? []) {
      if (!link.time_entry_id || !link.tag_id) {
        continue;
      }
      const tagRow = tagById().get(link.tag_id);
      if (!tagRow) {
        continue;
      }
      const list = map.get(link.time_entry_id) ?? [];
      list.push(tagRow);
      map.set(link.time_entry_id, list);
    }
    return map;
  });

  // 3b. Subscribe to raw feed events
  const feedEvents = createPowerSyncQuery<FeedEvent>({
    queryKey: () => queryKeys.chronos.timeline.feedEvents(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(feed_event)
        .where(and(eq(feed_event.user_id, userId()), ...liveFeedEventFilters()))
        .orderBy(desc(feed_event.created_at));
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  // Manual time-entry editing / creation
  const [isEntryDialogOpen, setIsEntryDialogOpen] = createSignal(false);
  const [editingEntryId, setEditingEntryId] = createSignal<string | null>(null);
  const [entryProjectId, setEntryProjectId] = createSignal("");
  const [entryTaskId, setEntryTaskId] = createSignal("");
  const [entryTagIds, setEntryTagIds] = createSignal<string[]>([]);
  const [entryDescription, setEntryDescription] = createSignal("");
  const [entryStart, setEntryStart] = createSignal("");
  const [entryEnd, setEntryEnd] = createSignal("");
  const [isSavingEntry, setIsSavingEntry] = createSignal(false);
  const [deleteEntryId, setDeleteEntryId] = createSignal<string | null>(null);

  const entryDialogTasks = createMemo(() =>
    (tasksQuery.data ?? []).filter((row) => row.project_id === entryProjectId()),
  );

  const toggleEntryTag = (tagId: string) => {
    setEntryTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    );
  };

  // Fills the creation form (without writing the URL; used by the public opener
  // and by hydration from the URL).
  const fillAddEntryForm = () => {
    setEditingEntryId(null);
    setEntryProjectId(projects.data?.[0]?.id ?? "");
    setEntryTaskId("");
    setEntryTagIds([]);
    setEntryDescription("");
    const now = new Date();
    setEntryEnd(toLocalInputValue(now.toISOString()));
    const HOUR_MS = 3_600_000;
    setEntryStart(toLocalInputValue(new Date(now.getTime() - HOUR_MS).toISOString()));
    setIsEntryDialogOpen(true);
  };

  // Hydrates the edit form from the entity (without writing the URL).
  const fillEditEntryForm = (entryId: string): boolean => {
    const entry = (timeEntries.data ?? []).find((te) => te.id === entryId);
    if (!entry) {
      return false;
    }
    setEditingEntryId(entry.id);
    setEntryProjectId(entry.project_id ?? "");
    setEntryTaskId(entry.task_id ?? "");
    setEntryTagIds((tagsByEntryId().get(entry.id) ?? []).map((row) => row.id));
    setEntryDescription(entry.description ?? "");
    setEntryStart(toLocalInputValue(entry.start_time));
    setEntryEnd(toLocalInputValue(entry.end_time));
    setIsEntryDialogOpen(true);
    return true;
  };

  const openAddEntryDialog = () => {
    fillAddEntryForm();
    setEntryUrl({ dialog: "entry-new" });
  };

  const openEditEntryDialog = (entryId: string) => {
    if (fillEditEntryForm(entryId)) {
      setEntryUrl({ dialog: "entry-edit", id: entryId });
    }
  };

  const openDeleteEntry = (entryId: string) => {
    setDeleteEntryId(entryId);
    setEntryUrl({ confirm: "delete-entry", id: entryId });
  };

  // URL → state. Hydrates the dialog indicated by the URL (pasted link, back/fwd).
  // To edit/delete, we need the row; while it arrives (local-first sync), the
  // dialog stays closed. Idempotent: we do not reopen it if it is already in that state.
  createEffect(() => {
    const dialog = entryUrlDialog();
    const confirm = entryUrlConfirm();
    const id = entryUrlId();
    // Track the data (entries/tags) so a deep-linked `?dialog=entry-edit`
    // hydrates when the row syncs (local-first).
    void timeEntries.data;
    void tagsByEntryId();

    // CC-19/CC-16: state↔URL reconciliation runs in `untrack` so the
    // effect reacts ONLY to EXTERNAL URL changes or to the entity arriving
    // through sync, NEVER to the dialog's local signals. If it tracked
    // `isEntryDialogOpen()`/`editingEntryId()`/`deleteEntryId()`, opening the dialog
    // state-first (`setIsEntryDialogOpen(true)` before async `navigate` writes
    // `?dialog=`) would retrigger the effect with the OLD URL (without the param) and
    // fall into the close branch → the dialog would BOUNCE closed (same race as
    // projects.tsx / WorkspaceSwitcher). Back/forward closing still
    // works: it changes the URL, which IS tracked.
    untrack(() => {
      if (dialog === "entry-new") {
        if (!isEntryDialogOpen() || editingEntryId() !== null) {
          fillAddEntryForm();
        }
        return;
      }
      if (dialog === "entry-edit" && id) {
        if (editingEntryId() !== id) {
          fillEditEntryForm(id);
        }
        return;
      }
      if (confirm === "delete-entry" && id) {
        if (deleteEntryId() !== id) {
          setDeleteEntryId(id);
        }
        return;
      }
      // No dialog in the URL: close whatever remains open.
      if (isEntryDialogOpen()) {
        setIsEntryDialogOpen(false);
      }
      if (deleteEntryId()) {
        setDeleteEntryId(null);
      }
    });
  });

  // The composite ID `${entryId}_${tagId}` is the format the server
  // expects when deleting associations (split by '_').
  const syncEntryTags = async (entryId: string, desired: string[]) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    const current = (entryTagsQuery.data ?? []).filter((l) => l.time_entry_id === entryId);
    const currentIds = new Set(current.map((l) => l.tag_id ?? ""));
    const desiredSet = new Set(desired);

    const toAdd = desired.filter((id) => !currentIds.has(id));
    const toRemove = current.filter((l) => l.tag_id && !desiredSet.has(l.tag_id));

    if (toAdd.length > 0) {
      await dbInstance.insert(time_entry_tag).values(
        toAdd.map((tagId) => ({
          id: `${entryId}_${tagId}`,
          time_entry_id: entryId,
          tag_id: tagId,
          created_at: new Date().toISOString(),
        })),
      );
    }
    await Promise.all(
      toRemove.map((l) => dbInstance.delete(time_entry_tag).where(eq(time_entry_tag.id, l.id))),
    );
  };

  const handleSaveEntry = async (e: Event) => {
    e.preventDefault();
    const dbInstance = drizzle();
    const currentUserId = userId();
    if (!dbInstance || !currentUserId) {
      return;
    }

    const startIso = fromLocalInputValue(entryStart());
    const endIso = fromLocalInputValue(entryEnd());
    if (!entryProjectId()) {
      showToast(t(msg`Please select a project`), "warning");
      return;
    }
    if (!startIso) {
      showToast(t(msg`The start time is not valid`), "warning");
      return;
    }
    if (endIso && endIso <= startIso) {
      showToast(t(msg`End time must be after start time`), "warning");
      return;
    }

    setIsSavingEntry(true);
    try {
      const editingId = editingEntryId();
      if (editingId) {
        // Capture the previous state for L1 undo (editing an already
        // stopped block is undoable; the durable revert is the history/feed one).
        const prevRows = await dbInstance
          .select()
          .from(time_entry)
          .where(eq(time_entry.id, editingId));
        const prev = prevRows.at(0);
        await patchRowVerified(dbInstance, time_entry, time_entry.id, editingId, {
          project_id: entryProjectId(),
          task_id: entryTaskId() || null,
          description: entryDescription().trim() || null,
          start_time: startIso,
          end_time: endIso,
          is_running: endIso ? 0 : 1,
        });
        await syncEntryTags(editingId, entryTagIds());
        if (prev) {
          registerUndo(t(msg`Entry updated`), async () => {
            await patchRowVerified(dbInstance, time_entry, time_entry.id, editingId, {
              project_id: prev.project_id,
              task_id: prev.task_id,
              description: prev.description,
              start_time: prev.start_time,
              end_time: prev.end_time,
              is_running: prev.is_running,
            });
          });
        } else {
          showToast(t(msg`Entry updated`), "success");
        }
      } else {
        if (!endIso) {
          showToast(t(msg`Manual entries need an end time`), "warning");
          return;
        }
        await ensureWorkspace(dbInstance, currentUserId);
        const newEntryId = generateId();
        await dbInstance.insert(time_entry).values({
          id: newEntryId,
          user_id: currentUserId,
          project_id: entryProjectId(),
          task_id: entryTaskId() || null,
          description: entryDescription().trim() || null,
          start_time: startIso,
          end_time: endIso,
          is_running: 0,
          source: "manual",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        await syncEntryTags(newEntryId, entryTagIds());
        registerUndo(t(msg`Entry added`), async () => {
          await patchRowVerified(dbInstance, time_entry, time_entry.id, newEntryId, {
            deleted_at: new Date().toISOString(),
          });
        });
      }
      setIsEntryDialogOpen(false);
      setEditingEntryId(null);
      setEntryUrl({});
    } catch (err) {
      console.error("Failed to save time entry", err);
      showToast(t(msg`Could not save the entry`), "error");
    } finally {
      setIsSavingEntry(false);
    }
  };

  const handleDeleteEntryConfirmed = async () => {
    const dbInstance = drizzle();
    const entryId = deleteEntryId();
    if (!dbInstance || !entryId) {
      return;
    }
    try {
      await patchRowVerified(dbInstance, time_entry, time_entry.id, entryId, {
        deleted_at: new Date().toISOString(),
      });
      registerUndo(t(msg`Entry deleted`), async () => {
        await patchRowVerified(dbInstance, time_entry, time_entry.id, entryId, {
          deleted_at: null,
        });
      });
    } catch (err) {
      console.error("Failed to delete time entry", err);
      showToast(t(msg`Could not delete the entry`), "error");
    } finally {
      setDeleteEntryId(null);
      setEntryUrl({});
    }
  };

  // 4. Group application logs into blocks of time
  const groupedActivityBlocks = createMemo<GroupedActivityBlock[]>(() => {
    const logs = appLogs.data ?? [];
    if (logs.length === 0) {
      return [];
    }

    // Sort logs chronologically (oldest first) to build groups
    const sortedLogs = logs.toSorted(
      (a, b) => new Date(a.start_time ?? "").getTime() - new Date(b.start_time ?? "").getTime(),
    );

    const groups: GroupedActivityBlock[] = [];
    let currentGroup: LogGroupState | null = null;

    for (const log of sortedLogs) {
      const logStart = new Date(log.start_time ?? "").getTime();
      const logDurationMs = (log.duration_seconds ?? LOG_SAMPLE_INTERVAL_S) * MS_IN_SEC;
      const logEnd = logStart + logDurationMs;

      if (currentGroup) {
        const lastGroupEnd = new Date(currentGroup.endTime).getTime();
        const gapSeconds = (logStart - lastGroupEnd) / MS_IN_SEC;

        if (gapSeconds <= FIVE_MINUTES_S) {
          addToGroup(currentGroup, log, logEnd, lastGroupEnd);
        } else {
          groups.push(buildGroup(currentGroup));
          currentGroup = createInitialGroup(log, logEnd);
        }
      } else {
        currentGroup = createInitialGroup(log, logEnd);
      }
    }

    if (currentGroup) {
      groups.push(buildGroup(currentGroup));
    }

    // Return descending (newest first)
    return groups.toReversed();
  });

  function buildGroup(g: {
    logIds: string[];
    startTime: string;
    endTime: string;
    appDurationMap: Map<string, number>;
    titles: Set<string>;
  }): GroupedActivityBlock {
    const totalSeconds = [...g.appDurationMap.values()].reduce((sum, s) => sum + s, 0);

    const appClassList = [...g.appDurationMap.entries()]
      .map(([name, duration]) => {
        const mins = Math.round(duration / SECONDS_IN_MINUTE);
        return {
          name,
          duration: mins > 0 ? `${mins}m` : `${duration}s`,
          letter: getAppLetter(name),
        };
      })
      .slice(0, MAX_APPS_DISPLAYED); // show top 5 apps

    const minsTotal = Math.round(totalSeconds / SECONDS_IN_MINUTE);
    const durationStr = minsTotal > 0 ? `${minsTotal} min` : `${totalSeconds} seg`;

    const titlesList = [...g.titles].filter(Boolean);
    const description =
      titlesList.length > 0
        ? titlesList.slice(0, MAX_TITLES_DISPLAYED).join(", ")
        : t(msg`Active application usage`);

    return {
      id: g.logIds[0] ?? generateId(),
      type: "unassigned",
      startTime: g.startTime,
      endTime: g.endTime,
      durationSeconds: totalSeconds,
      durationStr,
      appClassList,
      description,
      logIds: g.logIds,
    };
  }

  // 5. Combine registered time entries, feed events, and unassigned activity blocks into a single timeline list
  const timelineItems = createMemo<TimelineItem[]>(() => {
    const registered: TimelineItem[] = (timeEntries.data ?? []).map((entry) => {
      const proj = (projects.data ?? []).find((p) => p.id === entry.project_id);
      const start = new Date(entry.start_time ?? "").getTime();
      const end = entry.end_time ? new Date(entry.end_time).getTime() : Date.now();
      const diffMins = Math.round((end - start) / MS_IN_MINUTE);
      const duration = diffMins > 0 ? `${diffMins} min` : t(msg`Just now`);

      return {
        id: entry.id,
        type: "registered",
        taskName: entry.task_id ? taskNameById().get(entry.task_id) : undefined,
        tags: (tagsByEntryId().get(entry.id) ?? []).map((row) => ({
          id: row.id,
          name: row.name ?? "",
          color: row.color ?? "#f1b12b",
        })),
        projectName: proj?.name ?? t(msg`No Project`),
        projectCode: proj?.code ?? "N/A",
        projectColor: proj?.color ?? "bg-blue-500",
        description: entry.description ?? t(msg`No description`),
        startTime: entry.start_time
          ? new Date(entry.start_time).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
        endTime: entry.end_time
          ? new Date(entry.end_time).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
        duration,
        rawStartTime: entry.start_time ?? "",
      };
    });

    const unassigned: TimelineItem[] = groupedActivityBlocks().map((block) => ({
      id: block.id,
      type: "unassigned",
      projectName: t(msg`Unassigned Activity`),
      projectCode: "LOG",
      projectColor: "bg-muted-foreground/30",
      description: block.description,
      startTime: new Date(block.startTime).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      endTime: new Date(block.endTime).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      duration: block.durationStr,
      rawStartTime: block.startTime,
      appClassList: block.appClassList,
      logIds: block.logIds,
    }));

    const events: TimelineItem[] = (feedEvents.data ?? []).map((evt) => {
      const getMessage = () => {
        try {
          const parsed: unknown = JSON.parse(evt.event_data ?? "{}");
          if (parsed && typeof parsed === "object") {
            const data = parsed as { message?: unknown; description?: unknown };
            const message = typeof data.message === "string" ? data.message : null;
            const descText = typeof data.description === "string" ? data.description : null;
            return message ?? descText ?? t(t_focus_note);
          }
          return t(t_focus_note);
        } catch {
          return evt.event_data ?? t(t_focus_note);
        }
      };
      const message = getMessage();

      let pName = t(msg`Focus Event`);
      let pCode = "FOCUS";
      let pColor = "bg-[#ebb2ff]";
      if (evt.event_type === "status_update") {
        pName = t(t_focus_note);
        pCode = "NOTE";
        pColor = "bg-[#ceffdf]";
      } else if (evt.event_type === "focus_started") {
        pName = t(msg`Focus Started`);
        pCode = "START";
        pColor = "bg-[#f1b12b]";
      } else if (evt.event_type === "focus_completed") {
        pName = t(msg`Focus Completed`);
        pCode = "DONE";
        pColor = "bg-[#ebb2ff]";
      }

      const rawTime = evt.created_at ?? "";
      const timeStr = rawTime
        ? new Date(rawTime).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "";

      return {
        id: evt.id,
        type: "event",
        projectName: pName,
        projectCode: pCode,
        projectColor: pColor,
        description: message,
        startTime: timeStr,
        endTime: timeStr,
        duration: "Event",
        rawStartTime: rawTime,
      };
    });

    // Merge and sort descending
    return [...registered, ...unassigned, ...events].toSorted(
      (a, b) => new Date(b.rawStartTime).getTime() - new Date(a.rawStartTime).getTime(),
    );
  });

  const handleDismissLog = async (logIds: string[]) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }

    try {
      await Promise.all(
        logIds.map((id) =>
          patchRowVerified(dbInstance, application_log, application_log.id, id, {
            deleted_at: new Date().toISOString(),
          }),
        ),
      );
      showToast(t(msg`Activity dismissed`), "success");
    } catch (err) {
      console.error("Failed to dismiss logs", err);
      showToast(t(msg`Failed to dismiss activity`), "error");
    }
  };

  const handleAssignProject = async (item: TimelineItem, projectId: string) => {
    const dbInstance = drizzle();
    const currentUserId = userId();
    if (!dbInstance || !currentUserId || !item.logIds) {
      return;
    }

    try {
      await ensureWorkspace(dbInstance, currentUserId);
      const newEntryId = generateId();

      // Get target project
      const targetProj = (projects.data ?? []).find((p) => p.id === projectId);
      const projName = targetProj?.name ?? "Activity";

      // 1. Insert time entry
      await dbInstance.insert(time_entry).values({
        id: newEntryId,
        user_id: currentUserId,
        project_id: projectId,
        description: item.description,
        start_time: item.rawStartTime,
        end_time: new Date(
          new Date(item.rawStartTime).getTime() +
            item.logIds.length * LOG_SAMPLE_INTERVAL_S * MS_IN_SEC,
        ).toISOString(),
        is_running: 0,
        source: "manual",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });

      // 2. Mark application logs as assigned
      await Promise.all(
        item.logIds.map((logId) =>
          patchRowVerified(dbInstance, application_log, application_log.id, logId, {
            assigned_time_entry_id: newEntryId,
          }),
        ),
      );

      showToast(t(msg`Assigned to ${projName}`), "success");
      setActivePopoverId(null);
    } catch (err) {
      console.error("Failed to assign project to logs", err);
      showToast(t(msg`Assignment failed`), "error");
    }
  };

  return (
    <div class="space-y-6">
      <div class="flex items-center justify-between border-b border-white/5 pb-3">
        <span class="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {t(msg`Activity History`)}
        </span>
        <div class="flex items-center gap-2">
          <span class="text-[10px] text-muted-foreground font-mono bg-white/5 px-2 py-0.5 rounded-full">
            {t(msg`{count} total blocks`, { count: timelineItems().length })}
          </span>
          <Button
            size="sm"
            onClick={openAddEntryDialog}
            class="h-7 bg-white/5 hover:bg-white/10 text-foreground border border-white/10 flex items-center gap-1.5 cursor-pointer rounded-full font-mono text-[10px] uppercase tracking-wider"
          >
            <Plus class="h-3.5 w-3.5" />
            {t(msg`Add entry`)}
          </Button>
        </div>
      </div>

      <Show
        when={timelineItems().length > 0}
        fallback={
          <div class="text-center py-12 bg-white/[0.02] border border-white/5 rounded-2xl motion-safe:animate-fade-in">
            <ClockEmptyIllustration class="h-28 w-28 mx-auto mb-1" />
            <h3 class="text-sm font-bold text-foreground font-mono">
              {t(msg`No activity logged`)}
            </h3>
            <p class="text-xs text-muted-foreground mt-1 max-w-xs mx-auto font-mono">
              {t(msg`The window daemon will capture app window activity logs here.`)}
            </p>
          </div>
        }
      >
        <div class="relative pl-6 space-y-8 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-[1.5px] before:bg-white/10">
          <For each={timelineItems()}>
            {(item) => (
              <TimelineRow
                item={item}
                projects={projects.data ?? []}
                isPopoverOpen={() => activePopoverId() === item.id}
                setPopoverOpen={(open) => setActivePopoverId(open ? item.id : null)}
                getDotColor={getDotColor}
                appColorClass={appColorClass}
                onEdit={openEditEntryDialog}
                onDelete={openDeleteEntry}
                onDismiss={(logIds) => void handleDismissLog(logIds)}
                onAssign={(it, projectId) => void handleAssignProject(it, projectId)}
              />
            )}
          </For>
        </div>
      </Show>

      {/* Dialog: añadir/editar registro de tiempo */}
      <Dialog
        open={isEntryDialogOpen()}
        onOpenChange={(open) => {
          setIsEntryDialogOpen(open);
          if (!open) {
            setEditingEntryId(null);
            setEntryUrl({});
          }
        }}
        contentClass="bg-[#121212] border border-white/10 rounded-[24px] p-0 overflow-hidden max-w-md"
      >
        <div class="p-6 text-white">
          <DialogHeader showClose border={false} class="p-0 mb-4 text-white">
            <div class="flex items-start justify-between gap-3">
              <DialogTitle class="text-xl font-bold font-mono">
                {editingEntryId() ? t(msg`Edit Entry`) : t(msg`Add Entry`)}
              </DialogTitle>
              {/* Historial del bloque: quién lo editó y qué decía antes (facturación). */}
              <Show when={editingEntryId()}>
                <button
                  type="button"
                  onClick={() => setHistoryOpen(true)}
                  class="mr-8 inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 px-2.5 py-1 text-[11px] font-mono font-semibold text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  data-testid="entry-history-button"
                >
                  <History class="h-3.5 w-3.5" />
                  {t(msg`History`)}
                </button>
              </Show>
            </div>
            <DialogDescription class="text-xs text-white/60 font-mono mt-1">
              {editingEntryId()
                ? t(msg`Adjust the entry's project, description or times.`)
                : t(msg`Manually log a block of time you already worked.`)}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveEntry} class="space-y-4">
            <div class="space-y-1">
              <label
                for="entry-project"
                class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
              >
                {t(msg`Project`)}
              </label>
              <Select
                id="entry-project"
                aria-label={t(msg`Project`)}
                value={entryProjectId()}
                onChange={setEntryProjectId}
                class="font-mono"
                options={(projects.data ?? []).map((proj) => ({
                  value: proj.id,
                  label: `${proj.name} (${proj.code})`,
                }))}
                emptyAction={{
                  label: t(msg`Create a project`),
                  href: "/chronos/projects",
                  description: t(msg`No projects yet`),
                }}
              />
            </div>

            <Show when={entryDialogTasks().length > 0}>
              <div class="space-y-1">
                <label
                  for="entry-task"
                  class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
                >
                  {t(msg`Task (optional)`)}
                </label>
                <Select
                  id="entry-task"
                  aria-label={t(msg`Task (optional)`)}
                  value={entryTaskId()}
                  onChange={setEntryTaskId}
                  class="font-mono"
                  options={[
                    { value: "", label: `— ${t(msg`No task`)} —` },
                    ...entryDialogTasks().map((row) => ({
                      value: row.id,
                      label: row.name,
                    })),
                  ]}
                />
              </div>
            </Show>

            <Show when={(tagsQuery.data ?? []).length > 0}>
              <div class="space-y-1.5">
                <span class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono block">
                  {t(msg`Tags`)}
                </span>
                <div class="flex flex-wrap gap-1.5">
                  <For each={tagsQuery.data}>
                    {(tagRow) => {
                      const selected = () => entryTagIds().includes(tagRow.id);
                      return (
                        <button
                          type="button"
                          onClick={() => {
                            toggleEntryTag(tagRow.id);
                          }}
                          class="inline-flex items-center px-2.5 py-1 rounded-full border text-[10px] font-mono transition-all cursor-pointer"
                          style={{
                            "border-color": selected()
                              ? (tagRow.color ?? "#f1b12b")
                              : `${tagRow.color ?? "#f1b12b"}30`,
                            color: tagRow.color ?? "#f1b12b",
                            "background-color": selected()
                              ? `${tagRow.color ?? "#f1b12b"}26`
                              : "transparent",
                            opacity: selected() ? "1" : "0.6",
                          }}
                        >
                          {tagRow.name}
                        </button>
                      );
                    }}
                  </For>
                </div>
              </div>
            </Show>

            <div class="space-y-1">
              <label
                for="entry-description"
                class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
              >
                {t(msg`Description`)}
              </label>
              <input
                id="entry-description"
                type="text"
                placeholder={t(msg`What did you work on?`)}
                value={entryDescription()}
                onInput={(e) => setEntryDescription(e.currentTarget.value)}
                class="w-full bg-black/40 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 text-white placeholder-white/20 font-mono"
              />
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div class="space-y-1">
                <label
                  for="entry-start"
                  class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
                >
                  {t(msg`Start`)}
                </label>
                <input
                  id="entry-start"
                  type="datetime-local"
                  value={entryStart()}
                  onInput={(e) => setEntryStart(e.currentTarget.value)}
                  class="w-full bg-black/40 border border-white/10 rounded-[16px] px-3 py-3 text-xs focus:outline-none focus:ring-2 focus:ring-accent/50 text-white font-mono [color-scheme:dark]"
                />
              </div>
              <div class="space-y-1">
                <label
                  for="entry-end"
                  class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
                >
                  {t(msg`End`)}
                </label>
                <input
                  id="entry-end"
                  type="datetime-local"
                  value={entryEnd()}
                  onInput={(e) => setEntryEnd(e.currentTarget.value)}
                  class="w-full bg-black/40 border border-white/10 rounded-[16px] px-3 py-3 text-xs focus:outline-none focus:ring-2 focus:ring-accent/50 text-white font-mono [color-scheme:dark]"
                />
              </div>
            </div>

            <div class="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setIsEntryDialogOpen(false);
                  setEditingEntryId(null);
                  setEntryUrl({});
                }}
                class="px-5 py-2.5 rounded-full font-bold font-mono text-xs uppercase tracking-wider cursor-pointer border border-white/5 hover:bg-white/5"
              >
                {t(msg`Cancel`)}
              </Button>
              <Button
                type="submit"
                disabled={isSavingEntry() || !drizzle()}
                class="px-6 py-2.5 bg-primary text-black rounded-full font-bold font-mono text-xs uppercase tracking-wider cursor-pointer shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] hover:bg-primary/95"
              >
                {isSavingEntry() ? t(msg`Saving...`) : t(msg`Save`)}
              </Button>
            </div>
          </form>
        </div>
      </Dialog>

      {/* Confirmación de borrado de registro */}
      <ConfirmDialog
        title={t(msg`Delete entry?`)}
        description={t(msg`The time block will be removed from your history.`)}
        confirmLabel={t(msg`Delete`)}
        cancelLabel={t(msg`Cancel`)}
        variant="destructive"
        open={Boolean(deleteEntryId())}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteEntryId(null);
            setEntryUrl({});
          }
        }}
        onConfirm={() => void handleDeleteEntryConfirmed()}
      />

      {/* ACTIVITY-CORE — historial del bloque de tiempo en edición. */}
      <Show when={editingEntryId()}>
        {(id) => (
          <EntityHistoryDialog
            open={historyOpen()}
            onOpenChange={setHistoryOpen}
            entityType="time_entry"
            entityId={id()}
          />
        )}
      </Show>
    </div>
  );
}
